import { useEffect, useRef, useState, type FormEvent } from 'react';
import { FilePdf, PaperPlaneRight, Robot, X } from '@phosphor-icons/react';
import { abrirPdfEnPagina } from '../config/abrirPdf';
import {
  descargarDocumentoAsistente, preguntarAsistente, valorarAsistente, type AsistenteRespuesta,
} from '../services/api';
import styles from './AsistenteChat.module.css';

/**
 * El chatbot del PORTAL del cliente (pasos 92a y 92b). Responde con preguntas
 * frecuentes —el texto literal que escribió Adaptia—; si no hay, con un
 * documento PDF, citando documento y página (que se abre en esa página, D1);
 * y si no encuentra nada, ofrece abrir un ticket con la pregunta ya escrita.
 * Bajo una respuesta de documento el ticket está siempre a mano (D3): una
 * respuesta redactada se equivoca más que una literal.
 *
 * ⚠️ No abre el ticket él: pasa la pregunta y su id a `onAbrirTicket`, y el
 * portal lo abre con su formulario de siempre (`POST /incidencias`), que liga
 * la pregunta al ticket para el KPI «resuelta por la IA».
 *
 * Va en un botón FLOTANTE abajo a la derecha (D3, Jose, 2026-10-07), que
 * abre un panel; en el móvil, una hoja desde abajo.
 *
 * La conversación vive en memoria: al recargar empieza de cero. Lo que queda
 * en el servidor es el registro de preguntas (KPIs), no un historial para el
 * cliente.
 */

type Msg =
  | { tipo: 'cliente'; texto: string }
  | { tipo: 'bot'; r: AsistenteRespuesta; pregunta: string; util: boolean | null; ofrecer: boolean }
  | { tipo: 'aviso'; texto: string };

export function AsistenteChat({
  token,
  onAbrirTicket,
}: {
  token: string;
  onAbrirTicket: (pregunta: string, preguntaId: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const finRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    finRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [msgs, enviando]);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    const q = texto.trim();
    if (!q || enviando) return;
    setTexto('');
    setMsgs((m) => [...m, { tipo: 'cliente', texto: q }]);
    setEnviando(true);
    try {
      const r = await preguntarAsistente(token, q);
      setMsgs((m) => [...m, { tipo: 'bot', r, pregunta: q, util: null, ofrecer: r.ofrecerTicket }]);
    } catch (err) {
      setMsgs((m) => [...m, { tipo: 'aviso', texto: (err as Error).message }]);
    } finally {
      setEnviando(false);
    }
  };

  const valorar = async (i: number, util: boolean) => {
    const m = msgs[i];
    if (!m || m.tipo !== 'bot') return;
    setMsgs((xs) => xs.map((x, k) => (k === i && x.tipo === 'bot' ? { ...x, util } : x)));
    try {
      const v = await valorarAsistente(token, m.r.id, util);
      setMsgs((xs) => xs.map((x, k) => (k === i && x.tipo === 'bot' ? { ...x, ofrecer: v.ofrecerTicket } : x)));
    } catch {
      // La valoración es un dato de KPI: si no se guarda, el cliente no
      // tiene por qué enterarse. Se le sigue ofreciendo el ticket si dijo No.
      if (!util) setMsgs((xs) => xs.map((x, k) => (k === i && x.tipo === 'bot' ? { ...x, ofrecer: true } : x)));
    }
  };

  const chat = (
    <>
      <div className={styles.mensajes} aria-live="polite">
        {msgs.length === 0 && (
          <div className={styles.bot}>
            Pregúntame lo que necesites. Si no tengo la respuesta, te ayudo a abrir un ticket.
          </div>
        )}
        {msgs.map((m, i) => {
          if (m.tipo === 'cliente') return <div key={i} className={styles.cliente}>{m.texto}</div>;
          if (m.tipo === 'aviso') return <div key={i} className={styles.aviso} role="alert">{m.texto}</div>;
          const esFaq = m.r.resultado === 'faq' || m.r.resultado === 'documento';
          const fuente = m.r.fuente;
          return (
            <div key={i} className={styles.botCol}>
              <div className={styles.bot}>{m.r.respuesta}</div>
              {fuente && (
                <button
                  type="button"
                  className={styles.fuente}
                  title="Abrir el documento en esa página"
                  onClick={() => abrirPdfEnPagina(() => descargarDocumentoAsistente(token, fuente.documentoId), fuente.pagina)
                    .catch(() => setMsgs((xs) => [...xs, { tipo: 'aviso', texto: 'No se ha podido abrir el documento.' }]))}
                >
                  <FilePdf size={15} weight="duotone" />
                  <span className={styles.fuenteTxt}>{fuente.nombre} · p. {fuente.pagina}</span>
                </button>
              )}
              {esFaq && m.util === null && (
                <div className={styles.valorar}>
                  <span>¿Te ha servido?</span>
                  <button type="button" onClick={() => valorar(i, true)}>Sí</button>
                  <button type="button" onClick={() => valorar(i, false)}>No</button>
                </div>
              )}
              {esFaq && m.util === true && <div className={styles.gracias}>Gracias.</div>}
              {esFaq && m.util === false && (
                <div className={styles.gracias}>Vaya. Si quieres, abre un ticket y lo vemos.</div>
              )}
              {m.ofrecer && (
                <button type="button" className={styles.ticket} onClick={() => onAbrirTicket(m.pregunta, m.r.id)}>
                  Abrir ticket
                </button>
              )}
              {!m.ofrecer && m.r.resultado === 'documento' && (
                <button type="button" className={styles.ticketSec} onClick={() => onAbrirTicket(m.pregunta, m.r.id)}>
                  ¿No era esto? Abrir ticket
                </button>
              )}
            </div>
          );
        })}
        {enviando && <div className={styles.bot} aria-label="Buscando">…</div>}
        <div ref={finRef} />
      </div>
      <form className={styles.form} onSubmit={enviar}>
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escribe tu pregunta"
          aria-label="Escribe tu pregunta"
          maxLength={500}
        />
        <button type="submit" disabled={enviando || !texto.trim()} aria-label="Enviar">
          <PaperPlaneRight size={17} weight="fill" />
        </button>
      </form>
    </>
  );

  return (
    <>
      {abierto && (
        <section className={styles.panel} aria-label="Asistente de soporte">
          <div className={styles.panelHead}>
            <span className={styles.icono} aria-hidden><Robot size={18} weight="duotone" /></span>
            <div className={styles.panelTit}>
              <span className={styles.titulo}>Asistente de soporte</span>
              <span className={styles.sub}>Respuestas de tu equipo de IT</span>
            </div>
            <button type="button" className={styles.cerrar} onClick={() => setAbierto(false)} aria-label="Cerrar el asistente">
              <X size={16} weight="bold" />
            </button>
          </div>
          {chat}
        </section>
      )}
      {!abierto && (
        <button type="button" className={styles.burbuja} onClick={() => setAbierto(true)}>
          <Robot size={20} weight="duotone" />
          <span>¿Tienes una duda?</span>
        </button>
      )}
    </>
  );
}
